/**
 * DashboardProspectController — page /tableau-de-bord-prospect.html (§15.A, EPIC-028).
 *
 * Hydrate l'ensemble du dashboard depuis `GET /dashboard/prospect`
 * (payload agrégé livré par 28.2). Le controller est attaché au <body>
 * via le layout app ; tous les targets sont scopés au document.
 *
 * Blocs hydratés :
 *   - Greeting (welcomeName)
 *   - Suivi formalité INPI : 3 variantes selon onboardingStage
 *   - Bloc « Complétez votre profil » : visible si wizard ni complété ni skippé
 *   - Sidebar « Mes scénarios » : liste depuis simulationScenarios[]
 *   - Activité récente : timeline depuis recentActivity[]
 *   - Bloc « Aides identifiées » : pills ACRE / ARE / ARCE depuis aidEligibilityStatus
 *
 * Conventions :
 *   - Aucune construction HTML inline : <template> + cloneTemplate + fillSlots.
 *   - Vocabulaire UI : pas de NotaFacto / Sweego / PDP seul.
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

const STATUS_PILL_CLASSES = [
  "nm-pill--blue",
  "nm-pill--ochre",
  "nm-pill--olive",
  "nm-pill--red",
  "nm-pill--indigo",
];

const INPI_STATUS_DISPLAY = {
  pending: { label: "en instruction", pill: "nm-pill--blue" },
  pending_regularization: { label: "régularisation demandée", pill: "nm-pill--ochre" },
  regularization_processing: { label: "régularisation en cours", pill: "nm-pill--blue" },
  awaiting_deposit: { label: "en attente de dépôt", pill: "nm-pill--ochre" },
  accepted: { label: "acceptée", pill: "nm-pill--olive" },
  rejected_definitive: { label: "rejetée", pill: "nm-pill--red" },
};

const INPI_NEXT_STEP = {
  awaiting_deposit: "Dépôt à l'INPI déclenché par notre équipe sous 24-48 h.",
  pending: "SIRET attendu sous 8 à 15 jours ouvrés.",
  pending_regularization: "Compléments à fournir pour finaliser la formalité.",
  regularization_processing: "L'INPI réinstruit votre dossier après régularisation.",
  accepted: "Formalité acceptée.",
  rejected_definitive: "Notre équipe revient vers vous.",
};

const AID_STATUS_DISPLAY = {
  eligible: { label: "éligible", pill: "nm-pill--olive" },
  ineligible: { label: "non éligible", pill: "nm-pill--red" },
  pending: { label: "en cours", pill: "nm-pill--blue" },
  unknown: { label: "à déterminer", pill: "nm-pill--ochre" },
};

const EVENT_LABELS = {
  "inpi_formality.submitted": "Formalité INPI déposée au Guichet Unique",
  "inpi_formality.accepted": "Formalité INPI acceptée — SIRET délivré",
  "inpi_formality.rejected_definitive": "Formalité INPI rejetée",
  "inpi_formality.regularization_requested": "Régularisation demandée par l'INPI",
  "inpi_formality.regularization_submitted": "Régularisation transmise à l'INPI",
  "inpi_formality.regularization_processing": "Régularisation en cours d'instruction",
  "inpi_formality.created": "Dossier de création initialisé",
  "simulation.saved": "Nouveau scénario sauvegardé",
  "simulation.updated": "Scénario mis à jour",
};

export default class DashboardProspectController extends BaseController {
  static targets = [
    "welcomeName",
    // CTA hero adaptatif (has-ME)
    "ctaHero",
    // INPI variants
    "prePayment",
    "awaitingSiret",
    "pendingRegul",
    "regulCta",
    "inpiId",
    "depositedAt",
    "statusPill",
    "statusLabel",
    "nextStep",
    // Aid wizard prompt
    "aidWizardCard",
    // Scenarios
    "scenariosCount",
    "scenariosList",
    "scenariosEmpty",
    // Recent activity
    "activityList",
    "activityEmpty",
    // Aides identifiées
    "aidesCard",
    "aidAcrePill",
    "aidAcreLabel",
    "aidArePill",
    "aidAreLabel",
    "aidArcePill",
    "aidArceLabel",
  ];

  async connect() {
    let payload;
    try {
      payload = await api.dashboard.getProspect();
    } catch (_err) {
      // 401 → /connexion.html déjà géré par api.js. Autres erreurs : on garde
      // les blocs hidden — la page reste lisible (graceful degradation).
      return;
    }
    if (!payload) return;
    this._renderGreeting();
    this._renderCtaHero();
    this._renderInpi(payload.onboardingStage, payload.inpiFormality || null);
    this._renderProfileCompleteness(payload.profileCompleteness || {});
    this._renderScenarios(payload.simulationScenarios || []);
    this._renderActivity(payload.recentActivity || []);
    this._renderAides(payload.aidEligibilityStatus || {});
  }

  // ---------------------------------------------------------------------------
  // Greeting (le prénom est hydraté par app-header via /auth/me — ici on ne
  // touche au target que si présent et vide).
  // ---------------------------------------------------------------------------

  async _renderGreeting() {
    if (!this.hasWelcomeNameTarget || this.welcomeNameTarget.textContent.trim() !== "") {
      return;
    }
    try {
      const ctx = await api.getMe();
      const user = ctx?.user || null;
      const firstName = user?.firstName || user?.lastName || "";
      this.welcomeNameTarget.textContent = firstName ? `, ${firstName}` : "";
    } catch (_err) {
      // placeholder reste vide ; la page reste fonctionnelle.
    }
  }

  // ---------------------------------------------------------------------------
  // CTA hero adaptatif (EPIC-068.3) — selon prospectHasExistingMicro.
  //   true  → activation (demarrage-existante) prioritaire (tpl-hero-has-me)
  //   false → création (demarrage-creation) prioritaire (tpl-hero-no-me)
  //   null / indéterminé / échec → on garde le hero neutre statique en place.
  // Les deux chemins d'activation restent présents dans le DOM dans tous les
  // cas (chaque variante contient les deux liens).
  // ---------------------------------------------------------------------------

  async _renderCtaHero() {
    if (!this.hasCtaHeroTarget) return;
    let hasMe = null;
    try {
      const ctx = await api.getMeCached();
      hasMe = ctx?.organization?.prospectHasExistingMicro ?? null;
    } catch (_err) {
      // Échec /auth/me → on laisse le hero neutre statique (dégradation OK).
      return;
    }
    if (hasMe === null) return;

    const templateId = hasMe === true ? "tpl-hero-has-me" : "tpl-hero-no-me";
    this.ctaHeroTarget.replaceChildren(this.cloneTemplate(templateId));
  }

  // ---------------------------------------------------------------------------
  // INPI — 3 variantes selon onboardingStage + override régularisation.
  // ---------------------------------------------------------------------------

  _renderInpi(stage, formality) {
    if (stage === "pre_inpi_payment") {
      if (this.hasPrePaymentTarget) this.prePaymentTarget.hidden = false;
      return;
    }
    if (stage !== "awaiting_siret") {
      // awaiting_pdp / null → tout reste hidden (transition gérée ailleurs).
      return;
    }

    if (formality && formality.hasRegularizationRequest === true) {
      if (this.hasPendingRegulTarget) this.pendingRegulTarget.hidden = false;
      if (this.hasRegulCtaTarget) {
        const fid = formality.regularizationFormalityId || formality.id || "";
        this.regulCtaTarget.href = `/inpi-regularisation.html?formalityId=${encodeURIComponent(fid)}`;
      }
      return;
    }

    if (this.hasAwaitingSiretTarget) this.awaitingSiretTarget.hidden = false;
    if (!formality) return;

    if (this.hasInpiIdTarget) {
      const ref = formality.inpiReference || "";
      this.inpiIdTarget.textContent = ref && !ref.startsWith("LOCAL-") ? ref : "En attente de l'INPI";
    }
    if (this.hasDepositedAtTarget) {
      this.depositedAtTarget.textContent = this._formatDateLong(formality.createdAt);
    }
    if (this.hasStatusPillTarget && this.hasStatusLabelTarget) {
      const display = INPI_STATUS_DISPLAY[formality.status] || { label: formality.status || "—", pill: "nm-pill--blue" };
      STATUS_PILL_CLASSES.forEach((c) => this.statusPillTarget.classList.remove(c));
      this.statusPillTarget.classList.add(display.pill);
      this.statusLabelTarget.textContent = display.label;
    }
    if (this.hasNextStepTarget) {
      this.nextStepTarget.textContent = INPI_NEXT_STEP[formality.status] || "—";
    }
  }

  // ---------------------------------------------------------------------------
  // Bloc « Complétez votre profil » — masqué si wizard complété ou skippé.
  // ---------------------------------------------------------------------------

  _renderProfileCompleteness(profile) {
    if (!this.hasAidWizardCardTarget) return;
    const done = profile.aidWizardCompleted === true || profile.aidWizardSkipped === true;
    this.aidWizardCardTarget.hidden = done;
  }

  // ---------------------------------------------------------------------------
  // Sidebar « Mes scénarios ».
  // ---------------------------------------------------------------------------

  _renderScenarios(scenarios) {
    if (!this.hasScenariosListTarget) return;
    this.scenariosListTarget.replaceChildren();
    const count = scenarios.length;

    if (count === 0) {
      if (this.hasScenariosEmptyTarget) this.scenariosEmptyTarget.hidden = false;
      if (this.hasScenariosCountTarget) this.scenariosCountTarget.hidden = true;
      this.scenariosListTarget.hidden = true;
      return;
    }

    if (this.hasScenariosEmptyTarget) this.scenariosEmptyTarget.hidden = true;
    if (this.hasScenariosCountTarget) {
      const plural = count > 1 ? "s" : "";
      this.scenariosCountTarget.textContent = `Vous avez sauvegardé ${count} scénario${plural}. Comparez-les côte à côte depuis le simulateur.`;
      this.scenariosCountTarget.hidden = false;
    }

    for (const scenario of scenarios) {
      const fragment = this.cloneTemplate("tpl-scenario-item");
      this.fillSlots(fragment, {
        name: scenario.name || "Scénario sans nom",
        activityType: scenario.kind || "",
        updatedAt: this.formatTimeAgo(scenario.updatedAt),
      });
      const link = fragment.querySelector("[data-scenario-link]");
      if (link && scenario.id) {
        link.setAttribute("href", `/simulateur.html?scenarioId=${encodeURIComponent(scenario.id)}`);
      }
      this.scenariosListTarget.appendChild(fragment);
    }
    this.scenariosListTarget.hidden = false;
  }

  // ---------------------------------------------------------------------------
  // Activité récente — mapping eventType → libellé FR.
  // ---------------------------------------------------------------------------

  _renderActivity(events) {
    if (!this.hasActivityListTarget) return;
    this.activityListTarget.replaceChildren();

    if (events.length === 0) {
      if (this.hasActivityEmptyTarget) this.activityEmptyTarget.hidden = false;
      this.activityListTarget.hidden = true;
      return;
    }

    if (this.hasActivityEmptyTarget) this.activityEmptyTarget.hidden = true;

    for (const event of events) {
      const fragment = this.cloneTemplate("tpl-activity-item");
      this.fillSlots(fragment, {
        label: this._eventLabel(event),
        occurredAt: this.formatTimeAgo(event.occurredAt),
        eventType: this._eventSublabel(event),
      });
      this.activityListTarget.appendChild(fragment);
    }
    this.activityListTarget.hidden = false;
  }

  _eventLabel(event) {
    return EVENT_LABELS[event.eventType] || event.eventType || "Événement";
  }

  _eventSublabel(event) {
    const meta = event.metadata || {};
    if (event.domain === "simulation") {
      const parts = [];
      if (meta.title) parts.push(meta.title);
      if (meta.kind) parts.push(meta.kind);
      return parts.join(" · ");
    }
    if (event.domain === "inpi_formality") {
      if (meta.inpi_reference) return meta.inpi_reference;
      if (meta.reason) return meta.reason;
    }
    return "";
  }

  // ---------------------------------------------------------------------------
  // Bloc « Aides identifiées » — pills hydratées depuis aidEligibilityStatus.
  // Masqué si les 3 statuts sont à `unknown`.
  // ---------------------------------------------------------------------------

  _renderAides(aids) {
    const acre = aids.acre || "unknown";
    const are = aids.are || "unknown";
    const arce = aids.arce || "unknown";

    const allUnknown = acre === "unknown" && are === "unknown" && arce === "unknown";
    if (this.hasAidesCardTarget) {
      this.aidesCardTarget.hidden = allUnknown;
    }
    if (allUnknown) return;

    this._applyAidPill(this.aidAcrePillTarget, this.aidAcreLabelTarget, acre);
    this._applyAidPill(this.aidArePillTarget, this.aidAreLabelTarget, are);
    this._applyAidPill(this.aidArcePillTarget, this.aidArceLabelTarget, arce);
  }

  _applyAidPill(pillEl, labelEl, status) {
    const display = AID_STATUS_DISPLAY[status] || AID_STATUS_DISPLAY.unknown;
    if (pillEl) {
      STATUS_PILL_CLASSES.forEach((c) => pillEl.classList.remove(c));
      pillEl.classList.add(display.pill);
    }
    if (labelEl) {
      labelEl.textContent = display.label;
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _formatDateLong(iso) {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return "—";
      return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
    } catch (_err) {
      return "—";
    }
  }
}
